import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  getInitialProductList,
  getProductById,
  searchProductList,
  setPage,
} from "../../store/slices/ProductFinder/ProductFinderSlice";
import ItemDetailPopup from "../atoms/ItemDetailPopup/ItemDetailPopup";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import SmallSpinner from "../atoms/Loaders/SmallSpinner";
import ToggleButton from "../atoms/ToggleButton/ToggleButton";
import CheckBox from "../atoms/CheckBox/CheckBox";
import LoadingDots from "../atoms/Loaders/BouncingDots";
import ProductListRow from "../atoms/Items/ProductListItem";
import Button from "../atoms/Button";
import { useNavigate } from "react-router";
import DefaultProgressBar from "../atoms/ProgressBar/DefaultProgressBar";
import { INVENTORY_CLASSIFICATION } from "../../helpers/const";

const headers = [
  { title: "Product", visible: true, className: "" },
  { title: "Price", visible: true, className: "" },
  { title: "Last Ordered", visible: true, className: "" },
  { title: "", visible: true, className: " text-end" },
];

export default function ProductList() {
  const { productList, initialList, pending, page, filterObject } = useSelector(
    (state) => state.productFinder
  );

  const { cartItems, cart } = useSelector((state) => state.cart);
  const { prepaidWeight } = useSelector((state) => state.customer);

  const { viewCarts } = useSelector((state) => state.admin);

  const [searchVal, setSearchVal] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showDetail, setShowDetail] = useState(false);
  const [availOnly, setAvailOnly] = useState(false);
  const [searchXRef, setSearchXRef] = useState(true);
  const [searchDescription, setSearchDescription] = useState(true);
  const [searchProductCode, setSearchProductCode] = useState(true);
  const [searchOemPartNumbers, setSearchOemPartNumbers] = useState(true);

  const showItemDetail = (product_code) => {
    dispatch(getProductById({ product_code })).then((response) => {
      if (!response.error && response.payload) {
        setShowDetail(true);
      }
    });
  };

  const addPage = () => {
    dispatch(setPage(page + 1));
  };

  const removePage = () => {
    if (page - 1 < 1) return;
    dispatch(setPage(page - 1));
  };

  // Scroll event handler for infinite loading
  useEffect(() => {
    let isLoading = false;

    const handleScroll = (e) => {
      const target = e.target;
      // Only trigger when searching and not already loading
      if (!searchVal && filterObject.types.length === 0) return;
      if (pending || isLoading) return;

      // Check if we're near the bottom of the scrollable container
      if (target.scrollTop + target.clientHeight >= target.scrollHeight - 50) {
        isLoading = true;
        addPage();

        // Reset loading flag after a delay
        setTimeout(() => {
          isLoading = false;
        }, 1000);
      }
    };

    // Throttle the scroll event
    let ticking = false;
    const throttledScroll = (e) => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll(e);
          ticking = false;
        });
        ticking = true;
      }
    };

    // Find the scrollable container and add event listener
    const scrollContainer = document.querySelector(
      ".max-h-\\[600px\\].overflow-y-auto"
    );
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", throttledScroll);
      return () =>
        scrollContainer.removeEventListener("scroll", throttledScroll);
    }
  }, [searchVal, filterObject.types.length, pending]);

  const handleSearch = useCallback(
    (product_code) => {
      if (product_code || filterObject.types.length > 0)
        dispatch(
          searchProductList({
            product_code: product_code || 0,
            available_only: availOnly,
            search_xref: searchXRef,
            search_description: searchDescription,
            search_product_code: searchProductCode,
            search_oem_part_numbers: searchOemPartNumbers,
          })
        );
    },
    [
      dispatch,
      availOnly,
      searchXRef,
      searchDescription,
      searchProductCode,
      filterObject.types.length,
      searchOemPartNumbers,
    ]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      handleSearch(searchVal);
    }, 1000);

    return () => clearTimeout(handler);
  }, [
    searchVal,
    availOnly,
    searchXRef,
    searchDescription,
    searchProductCode,
    searchOemPartNumbers,
    page,
    handleSearch,
    filterObject,
  ]);

  useEffect(() => {
    if (!initialList) dispatch(getInitialProductList());
  }, [dispatch, initialList]);

  const goToCart = () => {
    navigate("/view-cart");
  };

  return (
    <>
      <ItemDetailPopup show={showDetail} setShow={setShowDetail} />

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold text-gray-900">
              Suggested Products
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Below are the recommended products based on your order history and
              search results.
            </p>
          </div>
          <Button
            type="positive"
            onClick={() => navigate("/view-history?history=items")}
            textContent="View Last 12 Month Product History"
          />
          {viewCarts && (
            <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none flex gap-2">
              <Button
                type="theme"
                onClick={goToCart}
                textContent={
                  cartItems.length > 0
                    ? `View Cart (${cartItems.length})`
                    : "View Cart"
                }
              />
            </div>
          )}
        </div>
        <div className="mt-8 flow-root bg-white/40 rounded-lg ring-1 ring-gray-200 shadow-sm">
          <div className="-my-2">
            {/* Fixed Header Section */}
            <div className="px-2 pt-2 pb-4 bg-white border-b border-gray-200">
              {/* First Row: Search, Toggle, Progress Bar */}
              <div className="flex flex-wrap items-start gap-4 mb-4">
                {/* Search Input */}
                <div className="flex flex-wrap items-start">
                  {/* Search Input */}
                  <div className="relative flex-grow max-w-sm min-w-[250px]">
                    <input
                      name="search"
                      type="search"
                      placeholder="Search"
                      value={searchVal}
                      onChange={(e) => setSearchVal(e.target.value)}
                      className="block w-full rounded-md bg-white py-1.5 pl-10 pr-10 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-[var(--primary-color)] sm:text-sm"
                    />
                    <MagnifyingGlassIcon
                      aria-hidden="true"
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-5 text-gray-400"
                    />
                    <SmallSpinner
                      show={pending}
                      className="border-l pl-2 absolute right-3 top-1/2 -translate-y-1/2 size-5 text-gray-400"
                    />
                  </div>

                  {/* Filters Applied Text */}
                  {filterObject.types.length > 0 && (
                    <div className="w-full max-w-sm pl-1 text-left">
                      <p className="text-sm text-gray-600 italic">
                        **Filters applied
                      </p>
                    </div>
                  )}
                </div>

                {/* Toggle */}
                <div className="flex items-center">
                  <ToggleButton toggleFunction={setAvailOnly} />
                </div>

                {/* Progress Bar */}
                <div className="flex-grow min-w-[200px]">
                  <DefaultProgressBar
                    currValue={
                      !cart.track_prepaid ? cart.total_weight : prepaidWeight
                    }
                    targetValue={prepaidWeight}
                    label={
                      `Free Freight` +
                      (Math.max(
                        Math.ceil(prepaidWeight - cart.total_weight),
                        0
                      ) > 0 && !cart.track_prepaid
                        ? ` in ${Math.max(Math.ceil(prepaidWeight - cart.total_weight), 0)} lbs`
                        : "!!!")
                    }
                    progressColor="bg-[var(--primary-color)]"
                    successColor="bg-green-500"
                  />
                </div>
              </div>

              {/* Second Row: Checkboxes */}
              <div className="flex flex-wrap gap-4 items-start">
                <fieldset className="flex flex-wrap gap-4 items-start">
                  <legend className="sr-only">Options</legend>
                  <CheckBox
                    label="Cross Ref Code"
                    checked={searchXRef}
                    onChange={setSearchXRef}
                    description="Check cross reference codes when searching"
                  />
                  <CheckBox
                    label="Description"
                    checked={searchDescription}
                    onChange={setSearchDescription}
                    description="Check descriptions when searching"
                  />
                  <CheckBox
                    label="Product Code"
                    checked={searchProductCode}
                    onChange={setSearchProductCode}
                    description="Check product codes when searching"
                  />
                  <CheckBox
                    label="OEM Part#s"
                    checked={searchOemPartNumbers}
                    onChange={setSearchOemPartNumbers}
                    description="Check OEM part numbers when searching"
                  />
                </fieldset>
              </div>

              {/* Inventory Legend */}
              <div className="flex items-center gap-4 mt-3 pt-2 border-t border-gray-100">
                <span className="text-xs text-gray-500 font-medium">
                  Inventory Status:
                </span>
                <div className="flex items-center gap-3">
                  {Object.values(INVENTORY_CLASSIFICATION).map(
                    (classification) => (
                      <div
                        key={classification.id}
                        className="flex items-center gap-1"
                      >
                        <div
                          className={`w-2 h-2 rounded-sm ${classification.bgColorClass}`}
                        ></div>
                        <span className="text-xs text-gray-600">
                          {classification.label}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>

            {/* Scrollable Table Section */}
            <div className="max-h-[600px] overflow-y-auto">
              <table className="min-w-full">
                <thead className="sticky top-0 bg-white z-10">
                  <tr>
                    {headers
                      .filter((txt) => txt.visible)
                      .map((head) => {
                        return (
                          <th
                            key={head.title}
                            id={head.title}
                            scope="col"
                            className={
                              "border-b border-gray-300 bg-white px-3 py-3.5 text-left text-sm font-semibold text-gray-900" +
                              head.className
                            }
                          >
                            {head.title}
                          </th>
                        );
                      })}
                  </tr>
                </thead>
                <tbody>
                  {productList?.length > 0 ? (
                    (searchVal || filterObject.types.length > 0
                      ? productList
                      : initialList
                    )?.map((product, idx) => {
                      return (
                        <ProductListRow
                          key={idx}
                          idx={idx}
                          cartItem={cartItems.find(
                            (ci) => ci.product_code === product.product_code
                          )}
                          product={product}
                          showItemDetail={showItemDetail}
                          onSearchProductCode={setSearchVal}
                        />
                      );
                    })
                  ) : (
                    <tr>
                      {headers
                        ?.filter((txt) => txt.visible)
                        .map((txt, idx) => {
                          return (
                            <td
                              key={idx}
                              className="flex-1 space-y-6 py-2 animate-pulse"
                            >
                              <div className="h-2 rounded bg-gray-200"></div>
                            </td>
                          );
                        })}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {pending && (searchVal || filterObject.types.length > 0) && (
              <div className="flex justify-center py-4">
                <LoadingDots className="bg-blue-500" />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
